/* ===========================================================
   SHELL — Header, Footer, MobileBottomNav, Breadcrumbs,
   PageShell, ProductTile, useReveal, useToast
   exposed on window.* so every page can pick what it needs
   =========================================================== */
import React from "react";
import { ProductTile } from "@/components/product-card";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useMe, useLogout, useCartCount, useUnreadNotificationCount } from "@/api/hooks";
import { useWishlist } from "@/hooks/useWishlist";

const { useState, useEffect, useRef, useLayoutEffect, useMemo } = React;

const NAV = [];

/* ----------------------------------------------------------
   Logo
---------------------------------------------------------- */
function Logo({ dark }) {
  const fg = dark ? "#fff" : "var(--ink)";
  return (
    <a href="/" className="logo" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
        <rect x="1" y="1" width="30" height="30" rx="8" fill="var(--brand-700)" />
        <path d="M9 10.5h3.6l5.4 7.6V10.5H21v11h-3.6L12 13.9V21.5H9z" fill="#fff" />
        <circle cx="24" cy="10" r="2.2" fill="var(--brand-300)" />
      </svg>
      <span style={{ fontWeight: 600, fontSize: 17, letterSpacing: "-0.02em", color: fg }}>
        nethra<span style={{ color: "#0a0a0a" }}>sap</span>
      </span>
    </a>
  );
}

/* ----------------------------------------------------------
   Announcement bar
---------------------------------------------------------- */
function AnnouncementBar() {
  return (
    <div className="announce">
      <div className="container announce-inner">
        <div className="announce-left">
          <span className="dot" />
          <span className="kicker">LIVE</span>
          <span className="hide-sm">Same-day dispatch on orders before 4:00 PM IST · Q2 retailer margin reset is live</span>
          <span style={{ display: "none" }} className="show-sm">Same-day dispatch · 4PM cutoff</span>
        </div>
        <div className="announce-right hide-sm">
          <a href="/pages/storefront/track-order.html" className="announce-link">Track order <Icons.ArrowR size={13} stroke={2} /></a>
          <span className="announce-sep" />
          <a href="#" className="announce-link"><Icons.Phone size={13} stroke={2} /> 1800-419-7700</a>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   Search pill / overlay
---------------------------------------------------------- */
function SearchPill({ onOpen }) {
  return (
    <button className="search-pill" onClick={onOpen} aria-label="Search products">
      <Icons.Search size={16} stroke={1.7} />
      <span className="search-placeholder">Search 12,400 SKUs by name, salt or HSN…</span>
      <span className="search-kbd">⌘K</span>
    </button>
  );
}

function SearchOverlay({ open, onClose }) {
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const popular = NETHRA_DATA.popularSearches;
  const submit = (e) => {
    e.preventDefault();
    const dest = "/pages/storefront/search.html?q=" + encodeURIComponent(q || "");
    window.location.href = dest;
  };
  return (
    <div className="search-overlay" onMouseDown={onClose}>
      <div className="search-overlay-card" onMouseDown={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="search-overlay-form">
          <Icons.Search size={18} stroke={1.7} />
          <input
            ref={inputRef}
            className="search-overlay-input"
            placeholder="Search 12,400 SKUs by name, salt or HSN…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <kbd className="search-kbd">ESC</kbd>
        </form>
        <div className="search-overlay-body">
          <div className="search-overlay-section">
            <div className="kicker">Popular searches</div>
            <div className="search-chips">
              {popular.map((p) => (
                <a key={p} href={"/pages/storefront/search.html?q=" + encodeURIComponent(p)} className="search-chip">{p}</a>
              ))}
            </div>
          </div>
          <div className="search-overlay-section">
            <div className="kicker">Quick links</div>
            <div className="search-quick">
              <a href="/pages/storefront/track-order.html"><Icons.Truck size={14} stroke={1.8} /> Track an order</a>
              <a href="/pages/storefront/categories.html"><Icons.Grid size={14} stroke={1.8} /> Browse categories</a>
              <a href="/pages/storefront/compare.html"><Icons.Hash size={14} stroke={1.8} /> Compare products</a>
              <a href="/pages/storefront/cart.html"><Icons.Cart size={14} stroke={1.8} /> My cart</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   Mega menu
---------------------------------------------------------- */
function MegaMenu({ open, onClose }) {
  if (!open) return null;
  const cats = NETHRA_DATA.categories;
  return (
    <div className="mega-menu" onMouseLeave={onClose}>
      <div className="container mega-grid">
        <div className="mega-col-cats">
          <div className="kicker">All categories</div>
          <div className="mega-cat-bento">
            {cats.map((c, i) => (
              <a
                key={c.id}
                href={"/pages/storefront/category.html?slug=" + c.slug}
                className={"mega-cat-tile mega-cat-tile-" + i}
              >
                <img src="/elementor-placeholder-image.png" alt={c.name} loading="lazy" />
                <div className="mega-cat-tile-overlay">
                  <span className="mega-cat-tile-name">{c.name}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   UserMenu — shows "Sign in" or the current user + a dropdown
---------------------------------------------------------- */
function UserMenu() {
  const { data: me, isLoading } = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (isLoading) {
    return <span className="user-menu-placeholder hide-sm" style={{ width: 84, height: 36 }} />;
  }

  if (!me) {
    return (
      <a className="btn btn-primary btn-sm hide-sm" href="/pages/auth/login.html">
        Sign in
      </a>
    );
  }

  const initials = (me.profile?.full_name || me.email)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

  return (
    <div className="user-menu" ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: "inline-flex", alignItems: "center", gap: 10,
          height: 36, padding: "0 6px 0 14px",
          border: "1px solid var(--line)", borderRadius: 999,
          background: "var(--paper-2)", color: "var(--ink)",
          cursor: "pointer", fontSize: 13, fontWeight: 500,
        }}
      >
        <span className="hide-sm" style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {me.profile?.full_name || me.email}
        </span>
        <span
          aria-hidden="true"
          style={{
            width: 28, height: 28, borderRadius: 999,
            background: "var(--brand-700)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 600,
          }}
        >
          {initials || "U"}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 8px)",
            minWidth: 240, background: "var(--paper-2)",
            border: "1px solid var(--line)", borderRadius: 12,
            boxShadow: "var(--shadow-lg, 0 10px 28px -12px rgba(40,54,24,0.25))",
            padding: 8, zIndex: 70,
          }}
        >
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)", marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{me.profile?.full_name || me.email}</div>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
              {me.role.toUpperCase()} · {me.status}
            </div>
          </div>
          <a href="/pages/storefront/wishlist.html" className="user-menu-item" style={menuItemStyle}>
            <Icons.Heart size={14} stroke={1.7} /> Wishlist
          </a>
          <a href="/pages/storefront/notifications.html" className="user-menu-item" style={menuItemStyle}>
            <Icons.Bell size={14} stroke={1.7} /> Notifications
          </a>
          <a href="/pages/storefront/track-order.html" className="user-menu-item" style={menuItemStyle}>
            <Icons.Truck size={14} stroke={1.7} /> Track an order
          </a>
          <button
            type="button"
            onClick={() => { setOpen(false); logout.mutate(undefined, { onSettled: () => window.location.reload() }); }}
            className="user-menu-item"
            style={{ ...menuItemStyle, width: "100%", background: "transparent", border: "none", color: "var(--rose, #b91c1c)", cursor: "pointer", textAlign: "left" }}
          >
            <Icons.ArrowR size={14} stroke={1.7} style={{ transform: "rotate(180deg)" }} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "10px 12px", borderRadius: 8,
  fontSize: 13, color: "var(--ink)", textDecoration: "none",
};

/* ----------------------------------------------------------
   Navbar
---------------------------------------------------------- */
function Navbar({ active, onSearch }) {
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const cartCount = useCartCount();
  const wishlistCount = useWishlist().count;
  const unreadCount = useUnreadNotificationCount();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSearch(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSearch]);

  return (
    <header className={"navbar" + (scrolled ? " is-scrolled" : "")}>
      <div className="container nav-inner">
        <div className="nav-left">
          <button className="icon-btn nav-mobile-btn" aria-label="Menu" onClick={() => setMobileOpen(true)}>
            <Icons.Menu size={20} />
          </button>
          <Logo />
          <nav className="nav-primary" aria-label="Primary">
            {NAV.map((n) => (
              <a key={n.key}
                 href={n.href}
                 className={"nav-link" + (active === n.key ? " is-active" : "")}
                 onMouseEnter={() => setMega(n.key === "categories")}
              >
                {n.label}
                {n.key === "categories" && <Icons.ChevD size={12} stroke={2} style={{ marginLeft: 2 }} />}
              </a>
            ))}
          </nav>
        </div>

        <div className="nav-center">
          <SearchPill onOpen={onSearch} />
        </div>

        <div className="nav-right">
          <button className="icon-btn nav-icon-mobile" aria-label="Search" onClick={onSearch}><Icons.Search size={18} /></button>
          <a className="icon-btn hide-md" href="/pages/storefront/compare.html" aria-label="Compare">
            <Icons.Compare size={18} />
          </a>
          <a className="icon-btn hide-md" href="/pages/storefront/wishlist.html" aria-label="Wishlist" style={{ position: "relative" }}>
            <Icons.Heart size={18} />
            {wishlistCount > 0 && <span className="cart-badge">{wishlistCount}</span>}
          </a>
          <a className="icon-btn" href="/pages/storefront/notifications.html" aria-label="Notifications" style={{ position: "relative" }}>
            <Icons.Bell size={18} />
            {unreadCount > 0 && <span className="cart-badge">{unreadCount}</span>}
          </a>
          <a className="icon-btn nav-cart" href="/pages/storefront/cart.html" aria-label="Cart">
            <Icons.Cart size={18} />
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </a>
          <span className="nav-divider hide-md" />
          <button className="lang-btn hide-md">
            <Icons.Globe size={14} stroke={1.8} />
            <span>EN</span>
            <Icons.ChevD size={12} stroke={1.8} />
          </button>
          <UserMenu />
        </div>

        {mega && <MegaMenu open={mega} onClose={() => setMega(false)} />}
      </div>

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} active={active} />
    </header>
  );
}

/* ----------------------------------------------------------
   Mobile drawer
---------------------------------------------------------- */
function MobileDrawer({ open, onClose, active }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  const cats = NETHRA_DATA.categories;
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <header className="drawer-head">
          <Logo />
          <button className="icon-btn" onClick={onClose} aria-label="Close"><Icons.X size={18} /></button>
        </header>
        <div className="drawer-body">
          <nav className="drawer-nav">
            {NAV.map((n) => (
              <a key={n.key} href={n.href} className={"drawer-link" + (active === n.key ? " is-active" : "")}>
                {n.label}
                <Icons.ChevR size={14} stroke={2} />
              </a>
            ))}
          </nav>
          <div className="drawer-section">
            <div className="kicker">Categories</div>
            <ul className="drawer-cat-list">
              {cats.map((c) => (
                <li key={c.id}>
                  <a href={"/pages/storefront/category.html?slug=" + c.slug}>
                    {c.name}
                    <span className="mono drawer-cat-count">{c.product_count.toLocaleString("en-IN")}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="drawer-section">
            <div className="kicker">Account</div>
            <a className="drawer-link" href="/pages/auth/login.html">Sign in</a>
            <a className="drawer-link" href="/pages/auth/signup.html">Create account</a>
            <a className="drawer-link" href="/pages/storefront/wishlist.html">Wishlist</a>
            <a className="drawer-link" href="/pages/storefront/notifications.html">Notifications</a>
            <a className="drawer-link" href="/pages/storefront/track-order.html">Track an order</a>
          </div>
        </div>
        <footer className="drawer-foot">
          <a href="#"><Icons.Phone size={13} /> 1800-419-7700</a>
          <a href="#"><Icons.Mail size={13} /> hello@nethrasap.in</a>
        </footer>
      </aside>
    </div>
  );
}

/* ----------------------------------------------------------
   Breadcrumbs
---------------------------------------------------------- */
function Breadcrumbs({ items = [] }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <div className="container breadcrumb-inner">
        <a href="/" className="breadcrumb-link"><Icons.Home size={13} stroke={1.7} /></a>
        {items.map((it, i) => (
          <span key={i} className="breadcrumb-item">
            <Icons.ChevR size={12} stroke={1.7} className="breadcrumb-sep" />
            {it.href ? <a className="breadcrumb-link" href={it.href}>{it.label}</a> : <span className="breadcrumb-current">{it.label}</span>}
          </span>
        ))}
      </div>
    </nav>
  );
}

/* ----------------------------------------------------------
   Page header (used by category, search, etc)
---------------------------------------------------------- */
function PageHeader({ eyebrow, title, description, stats, image, dark, children }) {
  return (
    <section className={"page-header" + (dark ? " is-dark" : "") + (image ? " has-image" : "")}>
      <div className="container page-header-inner">
        <div className="page-header-text">
          {eyebrow && (
            <div className="section-eyebrow">
              <span className="bar" />
              <span className="kicker">{eyebrow}</span>
            </div>
          )}
          <h1 className="page-header-title">{title}</h1>
          {description && <p className="page-header-desc">{description}</p>}
          {children}
          {stats && (
            <div className="page-header-stats">
              {stats.map((s, i) => (
                <div key={i} className="page-header-stat">
                  <div className="page-header-stat-v mono">{s.v}</div>
                  <div className="page-header-stat-k">{s.k}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {image && (
          <div className="page-header-image">
            <img src={image} alt="" />
            <span className="page-header-image-bracket" />
          </div>
        )}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------
   Footer
---------------------------------------------------------- */
function Footer() {
  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "Products", href: "/pages/storefront/products.html" },
    { label: "Categories", href: "/pages/storefront/categories.html" },
    { label: "Track order", href: "/pages/storefront/track-order.html" },
    { label: "Compare", href: "/pages/storefront/compare.html" },
  ];
  const customer = [
    { label: "Terms of Service",  href: "/pages/policy/terms.html" },
    { label: "Privacy Policy",    href: "/pages/policy/privacy.html" },
    { label: "Refund Policy",     href: "/pages/policy/refund.html" },
    { label: "Offline mode",      href: "/pages/system/offline.html" },
  ];
  const buyers = [
    { label: "For Retailers",      href: "#" },
    { label: "For Clinicians",     href: "#" },
    { label: "For Hospitals",      href: "#" },
    { label: "Become a supplier",  href: "#" },
  ];
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <Logo dark />
          <p className="footer-desc">
            India's audited healthcare supply platform — connecting licensed manufacturers directly to pharmacies, clinics and hospital procurement teams across 50 cities.
          </p>
          <div className="footer-newsletter">
            <div className="kicker" style={{ color: "rgba(255,255,255,0.6)" }}>Weekly procurement memo</div>
            <form className="footer-form" onSubmit={(e) => e.preventDefault()}>
              <input className="footer-input" placeholder="you@pharmacy.in" type="email" />
              <button className="btn btn-sm" style={{ background: "var(--brand-500)", color: "#fff" }}>
                Subscribe <Icons.ArrowR size={13} stroke={2} />
              </button>
            </form>
          </div>
        </div>

        <div>
          <div className="footer-h">Quick links</div>
          <ul className="footer-list">{quickLinks.map((l) => <li key={l.label}><a href={l.href}>{l.label}</a></li>)}</ul>
        </div>

        <div>
          <div className="footer-h">Buyer programs</div>
          <ul className="footer-list">{buyers.map((l) => <li key={l.label}><a href={l.href}>{l.label}</a></li>)}</ul>
        </div>

        <div>
          <div className="footer-h">Policies</div>
          <ul className="footer-list">{customer.map((l) => <li key={l.label}><a href={l.href}>{l.label}</a></li>)}</ul>
          <div className="footer-contact">
            <div><Icons.Phone size={12} stroke={1.8} /> <span className="mono">1800-419-7700</span></div>
            <div><Icons.Mail size={12} stroke={1.8} /> <span className="mono">hello@nethrasap.in</span></div>
            <div><Icons.Pin size={12} stroke={1.8} /> Palayamkottai · Tirunelveli</div>
          </div>
        </div>
      </div>

      <div className="footer-bar">
        <div className="container footer-bar-inner">
          <div className="footer-bar-left">
            <span>© 2026 Nethrasap Healthcare Pvt. Ltd.</span>
            <span className="footer-bar-sep" />
            <span className="mono">CDSCO/WL/2024/MH/8810</span>
            <span className="footer-bar-sep" />
            <span className="mono">GSTIN 27AABCN9012P1Z3</span>
          </div>
          <div className="footer-bar-right">
            <a href="/pages/policy/terms.html">Terms</a>
            <a href="/pages/policy/privacy.html">Privacy</a>
            <a href="/pages/policy/refund.html">Refund</a>
            <a href="#">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------------------------------
   Mobile top bar (compact replacement for the desktop navbar)
---------------------------------------------------------- */
function MobileTopBar({ onSearch }) {
  const cartCount = useCartCount();
  return (
    <header className="navbar is-mobile-top m-top">
      <Logo />
      <button className="m-top-search" onClick={onSearch} aria-label="Search">
        <Icons.Search size={14} stroke={1.7} />
        <span className="m-top-search-text">Search 12,400 SKUs…</span>
      </button>
      <a href="/pages/storefront/cart.html" className="m-top-cart" aria-label="Cart">
        <Icons.Cart size={16} stroke={1.7} />
        {cartCount > 0 && <span className="m-top-cart-badge">{cartCount}</span>}
      </a>
    </header>
  );
}

/* ----------------------------------------------------------
   Mobile bottom nav
---------------------------------------------------------- */
function MobileBottomNav({ active }) {
  const cartCount = useCartCount();
  // Map all known active keys (storefront + portal) to one of 5 tabs
  const TAB_MAP = {
    home: "home", products: "browse", categories: "browse", search: "browse",
    cart: "cart", track: "account", compliance: "account",
    dashboard: "account", profile: "account", addresses: "account",
    orders: "orders", enquiries: "orders", wishlist: "wishlist", notifications: "account",
  };
  const items = [
    { ic: "Home",    label: "Home",     k: "home",     href: "/" },
    { ic: "Grid",    label: "Browse",   k: "browse",   href: "/pages/storefront/products.html" },
    { ic: "Heart",   label: "Saved",    k: "wishlist", href: "/pages/storefront/wishlist.html" },
    { ic: "Cart",    label: "Cart",     k: "cart",     href: "/pages/storefront/cart.html", badge: cartCount },
    { ic: "Bell",    label: "Alerts",   k: "account",  href: "/pages/storefront/notifications.html" },
  ];
  const currentTab = TAB_MAP[active] || active || "home";
  return (
    <nav className="m-nav" aria-label="Mobile">
      {items.map((it) => {
        const Icon = Icons[it.ic];
        const isActive = currentTab === it.k;
        return (
          <a key={it.k} href={it.href} className={"m-nav-item" + (isActive ? " is-active" : "")}>
            <span className="m-nav-ic">
              <Icon size={19} stroke={1.7} />
              {it.badge ? <span className="m-nav-badge">{it.badge}</span> : null}
            </span>
            <span className="m-nav-label">{it.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

/* ----------------------------------------------------------
   Page shell — wraps everything
---------------------------------------------------------- */
function PageShell({ active, hideMobileNav = false, children }) {
  const [searchOpen, setSearchOpen] = useState(false);
  // mount reveal-on-scroll observer for any .reveal element
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" });
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
  return (
    <>
      <AnnouncementBar />
      <Navbar active={active} onSearch={() => setSearchOpen(true)} />
      <MobileTopBar onSearch={() => setSearchOpen(true)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <main className="page-main">{children}</main>
      <Footer />
      {!hideMobileNav && <MobileBottomNav active={active} />}
    </>
  );
}

/* ----------------------------------------------------------
   Helpers
---------------------------------------------------------- */
function getQuery(name) {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

function inr(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function findProduct(id) {
  return NETHRA_DATA.products.find((p) => p.id === id);
}

/* ----------------------------------------------------------
   Toast
---------------------------------------------------------- */
let toastSetter = null;
function ToastHost() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => { toastSetter = setToasts; return () => { toastSetter = null; }; }, []);
  return (
    <div className="toast-host">
      {toasts.map((t) => (
        <div key={t.id} className={"toast" + (t.kind ? " toast-" + t.kind : "")}>
          <Icons.Check size={14} stroke={2.4} />
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}
function toast(message, kind = "") {
  if (!toastSetter) return;
  const id = Math.random().toString(36).slice(2);
  toastSetter((arr) => [...arr, { id, message, kind }]);
  setTimeout(() => { toastSetter && toastSetter((arr) => arr.filter((x) => x.id !== id)); }, 2400);
}

export { Logo, Navbar, AnnouncementBar, Footer, MobileBottomNav, Breadcrumbs, PageHeader, PageShell, ToastHost, getQuery, inr, findProduct, toast };

